import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  getDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { toast } from "react-toastify";

// Fee Configuration Types
export const FEE_TYPES = {
  PAID_TRAINEE: "paid_trainee",
  INDIVIDUAL_INTERNSHIP: "individual_internship", 
  INDIVIDUAL_INTERN_STIPEND: "individual_intern_stipend",
  DEFAULT_COLLEGE_INTERNSHIP: "default_college_internship",
};

// Fee Configuration Categories
export const FEE_CATEGORIES = {
  INCOME: "income",
  EXPENSE: "expense",
};

const FEE_CONFIG_COLLECTION = "fee_configurations";

// Default fee configuration structure
const DEFAULT_FEE_CONFIG = {
  [FEE_TYPES.PAID_TRAINEE]: {
    type: FEE_TYPES.PAID_TRAINEE,
    name: "Paid Trainee Fee",
    description: "Training fee charged to paid trainees",
    amount: 25000,
    category: FEE_CATEGORIES.INCOME,
    currency: "INR",
    frequency: "one_time",
    isActive: true,
  },
  [FEE_TYPES.INDIVIDUAL_INTERNSHIP]: {
    type: FEE_TYPES.INDIVIDUAL_INTERNSHIP,
    name: "Individual Internship Fee", 
    description: "Fee charged for individual internships (not from colleges)",
    amount: 15000,
    category: FEE_CATEGORIES.INCOME,
    currency: "INR",
    frequency: "one_time",
    isActive: true,
  },
  [FEE_TYPES.INDIVIDUAL_INTERN_STIPEND]: {
    type: FEE_TYPES.INDIVIDUAL_INTERN_STIPEND,
    name: "Individual Intern Stipend",
    description: "Monthly stipend paid to individual interns",
    amount: 2000,
    category: FEE_CATEGORIES.EXPENSE,
    currency: "INR", 
    frequency: "monthly",
    isActive: true,
  },
  [FEE_TYPES.DEFAULT_COLLEGE_INTERNSHIP]: {
    type: FEE_TYPES.DEFAULT_COLLEGE_INTERNSHIP,
    name: "Default College Internship Fee",
    description: "Default fee for college internships (when college-specific fee not set)",
    amount: 12000,
    category: FEE_CATEGORIES.INCOME,
    currency: "INR",
    frequency: "one_time", 
    isActive: true,
  },
};

// Initialize default fee configurations
export const initializeFeeConfigurations = async () => {
  try {
    const feeConfigDoc = doc(db, FEE_CONFIG_COLLECTION, "default");
    const docSnap = await getDoc(feeConfigDoc);

    if (!docSnap.exists()) {
      await setDoc(feeConfigDoc, {
        configurations: DEFAULT_FEE_CONFIG,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      console.log("Default fee configurations initialized");
    }

    return DEFAULT_FEE_CONFIG;
  } catch (error) {
    console.error("Error initializing fee configurations:", error);
    return DEFAULT_FEE_CONFIG;
  }
};

// Get all fee configurations
export const getFeeConfigurations = async () => {
  try {
    const feeConfigDoc = doc(db, FEE_CONFIG_COLLECTION, "default");
    const docSnap = await getDoc(feeConfigDoc);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return data.configurations || DEFAULT_FEE_CONFIG;
    } else {
      // Initialize if doesn't exist
      return await initializeFeeConfigurations();
    }
  } catch (error) {
    console.error("Error fetching fee configurations:", error);
    toast.error("Failed to fetch fee configurations");
    return DEFAULT_FEE_CONFIG;
  }
};

// Get specific fee configuration
export const getFeeConfiguration = async (feeType) => {
  try {
    const configurations = await getFeeConfigurations();
    return configurations[feeType] || null;
  } catch (error) {
    console.error("Error fetching fee configuration:", error);
    return null;
  }
};

// Update fee configuration
export const updateFeeConfiguration = async (feeType, updates) => {
  try {
    const feeConfigDoc = doc(db, FEE_CONFIG_COLLECTION, "default");
    const docSnap = await getDoc(feeConfigDoc);
    
    let currentConfigs = DEFAULT_FEE_CONFIG;
    if (docSnap.exists()) {
      currentConfigs = docSnap.data().configurations || DEFAULT_FEE_CONFIG;
    }

    // Update the specific configuration
    const updatedConfigs = {
      ...currentConfigs,
      [feeType]: {
        ...currentConfigs[feeType],
        ...updates,
        amount: parseFloat(updates.amount) || currentConfigs[feeType]?.amount || 0,
        updatedAt: new Date(),
      },
    };

    await setDoc(feeConfigDoc, {
      configurations: updatedConfigs,
      updatedAt: serverTimestamp(),
    }, { merge: true });

    toast.success("Fee configuration updated successfully");
    return updatedConfigs[feeType];
  } catch (error) {
    console.error("Error updating fee configuration:", error);
    toast.error("Failed to update fee configuration");
    throw error;
  }
};

// Bulk update fee configurations
export const updateFeeConfigurations = async (configurations) => {
  try {
    const feeConfigDoc = doc(db, FEE_CONFIG_COLLECTION, "default");
    
    // Process and validate configurations
    const processedConfigs = {};
    Object.keys(configurations).forEach(feeType => {
      processedConfigs[feeType] = {
        ...configurations[feeType],
        amount: parseFloat(configurations[feeType].amount) || 0,
        updatedAt: new Date(),
      };
    });

    await setDoc(feeConfigDoc, {
      configurations: processedConfigs,
      updatedAt: serverTimestamp(),
    }, { merge: true });

    toast.success("Fee configurations updated successfully");
    return processedConfigs;
  } catch (error) {
    console.error("Error updating fee configurations:", error);
    toast.error("Failed to update fee configurations");
    throw error;
  }
};

// Get fee amount by type
export const getFeeAmount = async (feeType) => {
  try {
    const config = await getFeeConfiguration(feeType);
    return config?.amount || 0;
  } catch (error) {
    console.error("Error fetching fee amount:", error);
    return 0;
  }
};

// Helper function to get all fee amounts in a single call
export const getAllFeeAmounts = async () => {
  try {
    const configurations = await getFeeConfigurations();
    const amounts = {};
    
    Object.keys(configurations).forEach(feeType => {
      amounts[feeType] = configurations[feeType]?.amount || 0;
    });

    return amounts;
  } catch (error) {
    console.error("Error fetching all fee amounts:", error);
    return {
      [FEE_TYPES.PAID_TRAINEE]: 25000,
      [FEE_TYPES.INDIVIDUAL_INTERNSHIP]: 15000,
      [FEE_TYPES.INDIVIDUAL_INTERN_STIPEND]: 2000,
      [FEE_TYPES.DEFAULT_COLLEGE_INTERNSHIP]: 12000,
    };
  }
};

// Reset to default configurations
export const resetFeeConfigurations = async () => {
  try {
    const feeConfigDoc = doc(db, FEE_CONFIG_COLLECTION, "default");
    
    await setDoc(feeConfigDoc, {
      configurations: DEFAULT_FEE_CONFIG,
      updatedAt: serverTimestamp(),
    });

    toast.success("Fee configurations reset to defaults");
    return DEFAULT_FEE_CONFIG;
  } catch (error) {
    console.error("Error resetting fee configurations:", error);
    toast.error("Failed to reset fee configurations");
    throw error;
  }
};

// Format currency
export const formatCurrency = (amount, currency = "INR") => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);
};

// Get fee configuration display name
export const getFeeDisplayName = (feeType) => {
  const config = DEFAULT_FEE_CONFIG[feeType];
  return config?.name || feeType.replace(/_/g, ' ').toUpperCase();
};

// Validate fee configuration
export const validateFeeConfiguration = (config) => {
  const errors = [];

  if (!config.name || config.name.trim() === '') {
    errors.push('Name is required');
  }

  if (!config.amount || isNaN(parseFloat(config.amount)) || parseFloat(config.amount) < 0) {
    errors.push('Amount must be a valid positive number');
  }

  if (!config.category || !Object.values(FEE_CATEGORIES).includes(config.category)) {
    errors.push('Invalid category');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};